/**
 * Custom domains (migration 093, roadmap P1).
 *
 * A tenant points `demo.acme.com` at the Foxet box; we provision nginx + a
 * Let's Encrypt cert and the SPA served there mounts the public
 * captured-walkthrough player at `/` instead of `/w/<tenantSlug>/<slug>`.
 *
 * Browser-safe on purpose — this module is reachable from the web bundle, so
 * it must never touch `process`, `Buffer` or any other Node global at module
 * eval (see the shared-browser-safe rule).
 */
/**
 * Hosts the platform itself owns. A custom domain may never be one of these,
 * and the SPA never asks the API to resolve one — on these hosts it boots into
 * the normal dashboard router.
 *
 * Kept in CODE rather than env, same reasoning as FOXET_APP_IP in the API's
 * dns.ts: a misedited .env must not be able to turn a platform host into a
 * claimable custom domain.
 */
export const PLATFORM_DOMAIN_SUFFIXES = ['foxet.ai', 'foxet.local'];
/** Dev/preview hosts that are neither platform domains nor custom domains. */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]', '::1']);
/**
 * True for anything that is NOT a tenant's custom domain: foxet.ai and its
 * subdomains, localhost, and bare IPs. Fail-safe direction is "platform":
 * an unrecognised shape boots the dashboard rather than probing for a tenant.
 */
export function isPlatformHost(host) {
    if (!host)
        return true;
    const bare = host.toLowerCase().trim().split(':')[0];
    if (!bare)
        return true;
    if (LOCAL_HOSTS.has(bare))
        return true;
    // Bare IPv4 — the box's own address, never a customer hostname.
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(bare))
        return true;
    return PLATFORM_DOMAIN_SUFFIXES.some((s) => bare === s || bare.endsWith(`.${s}`));
}
/**
 * Hostname rules for a claimable custom domain. Deliberately stricter than
 * RFC 1123: lowercase only, at least two labels, no platform host, no
 * underscores or anything outside [a-z0-9.-] — the value is interpolated into
 * an nginx config and passed to a sudo script, so the character set is the
 * first line of defence and it is enforced here, in the bash script, AND by a
 * CHECK constraint in migration 093.
 */
export function isValidCustomDomain(host) {
    if (typeof host !== 'string')
        return false;
    const h = host.trim();
    if (h !== h.toLowerCase())
        return false;
    if (h.length < 4 || h.length > 253)
        return false;
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(h))
        return false;
    // Every label max 63 chars (the regex above allows longer).
    if (h.split('.').some((label) => label.length > 63))
        return false;
    if (isPlatformHost(h))
        return false;
    return true;
}
//# sourceMappingURL=domains.js.map